import React, { useState } from 'react'
import { Box, Stack, Typography, useTheme } from '@mui/material'

import { NAV_HEADER_HEIGHT } from './NavigationHeader'

import { font, space } from 'settings/tokens'

import { glassEdge } from 'utils/glass'

import { getEmbeddedKey } from 'utils/embeddedKey'

import type { Company } from 'types'

/** The brand mark's drawn height. The bar's own height stays `NAV_HEADER_HEIGHT`. */
const MARK_HEIGHT = 28

/**
 * The brand's mark at the top of a PUBLIC certificate.
 *
 * ## Why the brand and not the Vera mark
 *
 * The header answers one question — *who vouches for this object?* — and the
 * answer is the issuing brand, never the platform. That holds on every host,
 * which is the whole point: a certificate served from bare `app.vera.inc` shows
 * the same mark it shows on `<brand>.app.vera.inc`, because the company is a
 * fact of the RECORD and the hostname is an accident of routing. Vera's own
 * role is named once, in the footer, by `PoweredByVera` — and `hide_powered_by`
 * already governs whether even that appears.
 *
 * It exists because of the arrival, not for decoration. A customer taps a chip
 * on a physical object, lands on a domain they have never seen, and is asked
 * for an email address and their location before they are shown anything. With
 * no brand mark on the page that sequence is structurally indistinguishable
 * from a phishing page, and we are asking them to trust it at exactly the
 * moment we have given them the least reason to.
 *
 * ## Why it costs no layout
 *
 * `NavigationHeader` renders only for the owner and incoming-transfer views,
 * but `pageBodySx` reserves `DESKTOP_TOP` and `mediaColSx` reserves
 * `NAV_HEADER_HEIGHT` unconditionally — so on a public certificate the page was
 * already holding a bar-shaped hole open for a bar that never came. This fills
 * that hole at the same height, and the two reservations are handed a
 * `hasBrandHeader` flag so the space is spent once rather than twice.
 *
 * Correspondingly it does NOT render over `NavigationHeader`: that bar owns the
 * slot when it is present, and stacking two 72px bars on a 375-wide phone
 * spends a fifth of the viewport on chrome before the photograph starts. The
 * owner already knows whose certificate they are holding; the stranger who just
 * tapped the tag does not, and this is for them.
 */
export interface CertificateBrandHeaderProps {
  company?: Company | null
}

/**
 * Whether the bar will render anything — exported because the PAGE has to know.
 *
 * `pageBodySx` and `mediaColSx` spend the bar's height out of the reservation
 * they already hold, so the layout's belief about whether a bar exists has to
 * match the bar's own. Two independent copies of this condition would drift the
 * first time one of them changed, and the symptom would be a 72px hole (or a
 * missing 72px) rather than an error — so there is one predicate and both
 * callers read it.
 *
 * Suppressed inside the install snippet's embed: there the certificate renders
 * on the brand's OWN storefront, under the brand's own site header, so the mark
 * would print twice within a scroll of itself — a rendering fault, not
 * reassurance. The trust problem this bar solves is specific to an unfamiliar
 * domain, and an embed by definition is not one.
 *
 * `getEmbeddedKey()` is synchronous (a `<script data-embedded-key>` the snippet
 * writes into the host page), so there is no loading beat in which the bar
 * could flash onto a brand's storefront before being withdrawn — the same
 * property `isVeraOwnedSurface` depends on, and for the same reason.
 */
export const hasCertificateBrandHeader = (
  company?: Company | null,
  /*
   * Injectable for the STUDIO PREVIEW, which has no embed snippet of its own and so
   * would always read as hosted. Studio passes the install location the brand picked,
   * so the preview can show what customers at THAT address see. `undefined` means "ask
   * the page", which is every real render. Mirrors `extractSubdomain`'s
   * `embeddedKeyOverride` in `utils/subdomain.ts` — same need, same shape.
   */
  embeddedOverride?: string | null,
): boolean => {
  const embedded =
    embeddedOverride !== undefined ? embeddedOverride : getEmbeddedKey()
  if (embedded) return false
  // Nothing to show is better than a blank 72px band: with neither a mark nor a
  // name there is no brand claim to make, and an empty bar just pushes the
  // photograph down for nothing.
  return Boolean(company?.name?.trim() || company?.logo)
}

export const CertificateBrandHeader = ({ company }: CertificateBrandHeaderProps) => {
  const theme = useTheme()
  const [markFailed, setMarkFailed] = useState(false)

  if (!hasCertificateBrandHeader(company)) return null

  const name = company?.name?.trim()
  const logo = company?.logo

  const showMark = Boolean(logo) && !markFailed

  return (
    <Stack
      alignItems='center'
      justifyContent='center'
      sx={{
        height: `${NAV_HEADER_HEIGHT}px`,
        px: `${space.lg}px`,
        flexShrink: 0,
        backgroundColor: theme.palette.background.default,
        // The hairline carries the no-photograph case, where the bar would
        // otherwise dissolve into the placeholder beneath it. With a photograph
        // the image's own edge does this work and the line is simply invisible
        // against it.
        //
        // `glassEdge`, NOT a hand-written `1px solid ${theme.palette.divider}`:
        // `#vera-shadow-root * { border-color: … }` in index.css has ID
        // specificity and silently discards an sx border COLOUR, so a
        // hand-rolled hairline renders in the reset's colour while reading
        // correctly in the source. The token carries the `!important` that
        // every other hairline in this app relies on.
        borderBottom: glassEdge(theme),
      }}
    >
      {showMark ? (
        <Box
          component='img'
          src={logo as string}
          alt={name ?? 'Brand'}
          // `onError` rather than trusting the URL: a logo that 404s would
          // otherwise leave a broken-image glyph sitting where the brand's mark
          // should be, which is worse than the wordmark fallback below.
          onError={() => setMarkFailed(true)}
          sx={{
            display: 'block',
            maxHeight: `${MARK_HEIGHT}px`,
            // Marks are authored at wildly different aspect ratios — a square
            // monogram and a long wordmark both arrive here — so both axes are
            // capped and `contain` decides which one binds.
            maxWidth: '180px',
            objectFit: 'contain',
          }}
        />
      ) : (
        <Typography
          fontSize={font.body}
          fontWeight={500}
          // Letter-spaced small caps so the fallback reads as a WORDMARK rather
          // than as a line of body copy that happens to be a company name.
          sx={{
            textTransform: 'uppercase',
            letterSpacing: '0.08em',
            textAlign: 'center',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            maxWidth: '100%',
          }}
        >
          {name}
        </Typography>
      )}
    </Stack>
  )
}

export default CertificateBrandHeader
